<script setup lang="ts">
import { useRoute } from "vue-router";
import { useModyApi } from "~/middelware/ModyApi";
import type { MenuItem } from "primevue/menuitem";
import { Category } from "#imports";
type FilterOptions = "Name aufsteigend" | "Name absteigend"

const route = useRoute();
const api = useModyApi();
const filterOptions = ref<FilterOptions[]>(["Name aufsteigend", "Name absteigend"]);
const selectedFilterOption = ref<FilterOptions>("Name aufsteigend");

definePageMeta({
        middleware: "legacy-category-redirect"
    }
)

const homeMenuItem = ref<MenuItem>({
  icon:"pi pi-home",
  route: "/shop"
})
const breadCrumbMenuItems = ref<MenuItem[]>([])

const categorySlug = computed(() => route.params.slug as string)

const { data:category } = await useAsyncData(
  `category-${categorySlug.value}`,
  async () => {
    const p = await api.getCategoryBySlug(categorySlug.value)
    console.log(route.name)
    if(p.id == 0 && route.name == "shop-category-slug") useRouter().push("/shop")
    return p.toJSON()
  },
  { watch: [categorySlug] }
)

const { data:products } = await useAsyncData(
  `products-${categorySlug.value}`,
  async () => {
    const p = await api.getCategoryProducts(categorySlug.value);
    return p.map(value => {return value.toJSON()})
  },
  { watch: [categorySlug] }
)

const sortedProducts = computed(()=>{
  if(!products.value) return []
  const productList = [...products.value]
  if(selectedFilterOption.value === "Name absteigend"){
    return productList.sort((a,b)=>{
      return b.Name.localeCompare(a.Name)
    })
  }
  else if(selectedFilterOption.value === "Name aufsteigend"){
    return productList.sort((a,b)=>{
      return a.Name.localeCompare(b.Name)
    })
  }
  return productList
})

const productCountLabel = computed(() => {
  const count = sortedProducts.value.length
  return `${count} ${count === 1 ? "Produkt" : "Produkte"}`
})

const validSubcategories = computed(() => {
  return (category.value?.subCategories || []).filter(subcategory => Boolean(subcategory.slug))
})


watchEffect(() => {
    breadCrumbMenuItems.value = []
    if (category.value){
      addBreadcrumbForCategory(category.value)
    }
})

function addBreadcrumbForCategory(category:Category){

  breadCrumbMenuItems.value.unshift({
    label:category.name,
    route:"/shop/category/"+category.slug
  })
  if(category.parentCategory){
    addBreadcrumbForCategory(category.parentCategory)
  }
}

</script>
<template>
  <main class="category-page">
    <div class="category-shell">
      <Breadcrumb class="category-breadcrumb" :home="homeMenuItem" :model="breadCrumbMenuItems" >
        <template #item="{ item, props }">
          <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" @click="navigate">
              <span :class="[item.icon, 'text-color']" />
              <span class="font-semibold">{{ item.label }}</span>
            </a>
          </router-link>
          <a v-else :href="item.url" :target="item.target" v-bind="props.action">
            <span class="text-color">{{ item.label }}</span>
          </a>
        </template>
      </Breadcrumb>

      <header class="category-header">
        <h1>{{ category?.name }}</h1>
        <p v-if="category?.description" class="category-description">{{ category.description }}</p>
        <p v-else class="category-description">Professionelle Veranstaltungstechnik für dein Event – einzeln oder als Teil einer abgestimmten Gesamtlösung.</p>

        <nav v-if="validSubcategories.length" class="subcategory-list" aria-label="Unterkategorien">
          <NuxtLink v-for="subcategory in validSubcategories" :key="subcategory.id" :to="`/shop/category/${subcategory.slug}`">
            {{ subcategory.name }}
          </NuxtLink>
        </nav>
      </header>

      <section class="category-toolbar" aria-label="Produktliste steuern">
        <div>
          <strong>{{ productCountLabel }}</strong>
          <span>in {{ category?.name }}</span>
        </div>
        <div class="sort-control">
          <label for="category-sort">Sortierung</label>
          <Select inputId="category-sort" v-model="selectedFilterOption" :options="filterOptions" size="small" />
        </div>
      </section>

      <details class="category-navigation">
        <summary>
          <span><i class="pi pi-sitemap" aria-hidden="true"></i> Andere Sortimentsbereiche</span>
          <i class="pi pi-chevron-down navigation-chevron" aria-hidden="true"></i>
        </summary>
        <ShopSideMenu :show-title="false" />
      </details>

      <section v-if="sortedProducts.length" class="product-grid" :aria-label="`Produkte in ${category?.name}`">
        <ShopProductItem v-for="product in sortedProducts" :key="product.id" :product="product" />
      </section>
      <section v-else class="empty-category" role="status">
        <i class="pi pi-box" aria-hidden="true"></i>
        <h2>Aktuell keine Produkte in dieser Kategorie</h2>
        <p>Wir stellen trotzdem gerne eine passende Lösung für dein Event zusammen.</p>
        <NuxtLink to="/shop/request">Unverbindlich anfragen <i class="pi pi-arrow-right" aria-hidden="true"></i></NuxtLink>
      </section>

      <section class="category-consultation">
        <div>
          <h2>Du bist nicht sicher, welches Produkt passt?</h2>
          <p>Beschreibe kurz dein Vorhaben. Wir kombinieren die passende Technik und prüfen die Verfügbarkeit persönlich.</p>
        </div>
        <NuxtLink to="/shop/request">Unverbindlich anfragen <i class="pi pi-arrow-right" aria-hidden="true"></i></NuxtLink>
      </section>
    </div>
  </main>
</template>
<style scoped lang="scss">
.category-page{
  min-height: 100vh;
  padding-bottom: clamp(4rem, 8vw, 7rem);
  color: white;
  background: $public-bg;
}

.category-shell{
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: 0 1.5rem;
}

.category-breadcrumb{
  padding: 1.35rem 0;
  border: 0;
  background: none;

  :deep(.p-breadcrumb-list){
    flex-wrap: wrap;
  }

  :deep(.p-breadcrumb-item-link){
    color: rgb(168 162 158);
    font-size: 0.86rem;
  }

  :deep(.p-breadcrumb-item-link:hover){
    color: white;
  }
}

.category-header{
  max-width: 820px;
  padding: clamp(1.25rem, 3vw, 2.5rem) 0 clamp(1.75rem, 4vw, 3rem);

  h1{
    margin: 0;
    font-size: clamp(2.5rem, 6vw, 4.35rem);
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.025em;
    text-wrap: balance;
  }
}

.category-description{
  max-width: 720px;
  margin: 1.3rem 0 0;
  color: $public-text-muted;
  font-size: clamp(1rem, 2vw, 1.15rem);
  line-height: 1.65;
}

.subcategory-list{
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.35rem;

  a{
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0.55rem 0.8rem;
    border: 1px solid $public-border;
    border-radius: $public-radius-control;
    color: rgb(231 229 228);
    background: transparent;
    font-size: 0.82rem;
    font-weight: 650;
    text-decoration: none;

    &:hover,
    &:focus-visible{
      border-color: rgba(213, 150, 69, 0.45);
      background: rgba(255, 255, 255, 0.035);
    }

    &:focus-visible{
      outline: 2px solid #E39737;
      outline-offset: 3px;
    }
  }
}

.category-toolbar{
  display: flex;
  gap: 1.5rem;
  align-items: end;
  justify-content: space-between;
  padding: 1.15rem 0;
  border-block: 1px solid rgba(255, 255, 255, 0.09);

  > div:first-child{
    display: grid;
    gap: 0.15rem;

    strong{
      font-size: 1rem;
    }

    span{
      color: rgb(120 117 115);
      font-size: 0.75rem;
    }
  }
}

.sort-control{
  display: flex;
  gap: 0.75rem;
  align-items: center;

  label{
    color: rgb(168 162 158);
    font-size: 0.78rem;
    font-weight: 650;
  }

  :deep(.p-select){
    min-width: 210px;
  }
}

.category-navigation{
  margin-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: $public-radius-card;
  background: rgba(27, 27, 29, 0.6);

  summary{
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1rem;
    color: rgb(214 211 209);
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 680;
    list-style: none;

    &::-webkit-details-marker{ display: none; }

    span{
      display: flex;
      gap: 0.65rem;
      align-items: center;
    }

    span i{ color: #E39737; }

    &:focus-visible{
      border-radius: 12px;
      outline: 2px solid #E39737;
      outline-offset: 3px;
    }
  }

  &[open] .navigation-chevron{ transform: rotate(180deg); }

  > :deep(.menuContainer){
    max-width: none;
    margin: 0;
    padding: 0.4rem 1rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.07);
  }

  :deep(.p-panelmenu-header-link),
  :deep(.p-panelmenu-item-link){
    padding-block: 0.7rem;
    font-size: 0.84rem;
  }
}

.navigation-chevron{
  color: rgb(168 162 158);
  font-size: 0.72rem;
  transition: transform 180ms ease;
}

.product-grid{
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
}

.empty-category{
  margin-top: 1.5rem;
  padding: clamp(3rem, 7vw, 5rem) 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: $public-radius-large;
  text-align: center;
  background: #191a1d;

  > i{
    color: #E39737;
    font-size: 2rem;
  }

  h2{
    margin: 1rem 0 0;
    font-size: 1.5rem;
  }

  p{
    margin: 0.7rem 0 1.4rem;
    color: rgb(168 162 158);
  }

  a{
    color: #E39737;
    font-weight: 700;
    text-decoration: none;
  }
}

.category-consultation{
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: space-between;
  margin-top: clamp(2.75rem, 6vw, 4.5rem);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border: 1px solid $public-border-strong;
  border-radius: $public-radius-large;
  background: $public-surface;

  > div{ max-width: 700px; }

  h2{
    margin: 0;
    font-size: clamp(1.7rem, 3.2vw, 2.45rem);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.03em;
  }

  > div > p:last-child{
    margin: 0.9rem 0 0;
    color: rgb(168 162 158);
    line-height: 1.6;
  }

  > a{
    flex: 0 0 auto;
    min-height: 48px;
    padding: 0.75rem 1.2rem;
    display: inline-flex;
    gap: 0.55rem;
    align-items: center;
    justify-content: center;
    border-radius: $public-radius-control;
    color: #111214;
    background: #E39737;
    font-weight: 750;
    text-decoration: none;

    &:hover,
    &:focus-visible{ background: #efa94d; }

    &:focus-visible{
      outline: 2px solid white;
      outline-offset: 3px;
    }
  }
}

@media (max-width: 900px){
  .product-grid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 639px){
  .category-shell{
    padding: 0 1rem;
  }

  .category-breadcrumb{
    padding-block: 1rem;
  }

  .category-header{
    padding-top: 1rem;
    padding-bottom: 1.5rem;
  }

  .category-consultation{
    align-items: stretch;
    flex-direction: column;
  }

  .sort-control{
    position: relative;

    label{
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      white-space: nowrap;
    }

    :deep(.p-select){
      width: 185px;
      min-width: 185px;
    }
  }

  .product-grid{
    grid-template-columns: 1fr;
  }

  .category-consultation > a{
    width: 100%;
  }
}
</style>
